import moment from 'moment';
import Header from './Header';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import ApplyBtns, {ApplyButton} from './ApplyButtons';
import {memo, useEffect, useRef, useState} from 'react';
import {cleanUrl, isCSR} from '@jobseeker/lotus/lib/utils';
import {Box, Button, Typography} from '@mui/material';
import {JOBDETAIL, ROUTE_JOB_DETAIL} from '@constants';
import {VersionVariants} from '@jobseeker/lotus/lib/enums';
import type {AppState} from '@jobseeker/common-core/lib/types';
import {handleJobApplicationOnHirist} from '@utils/applyJobUtils';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {JobOverviewCard, Loader} from '@jobseeker/lotus/lib/components';
import useInterceptor from '@jobseeker/common-core/lib/hooks/useInterceptor';
import {
  getUbaPageName,
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/lib/utils';
import type {JobDetail as JB} from '@jobseeker/common-core/lib/stateModels/job/jobdetail.models';
import {
  fetchSimilarJobs,
  saveJob,
} from '@jobseeker/common-core/lib/reducers/job/thunkServices/jobfeed';
import {
  fetchJobInsights,
  getMagicRank,
} from '@jobseeker/common-core/lib/reducers/job/thunkServices/jobdetail';
import Image from 'next/image';
import {HiristInfoIcon} from '@jobseeker/lotus/lib/assets/icons';
import {ReportJobDialog} from '@jobseeker/feature-job/lib/components';
import {jobDetailService} from '@jobseeker/common-core/lib/services';
import {useIsLargeDevice} from '@jobseeker/lotus/lib/hooks';
import BlogsPanel from '@jobseeker/feature-ads/lib/components/BlogsPanel';
import JobsByLocation from './JobsByLocation';
import InjectSearchOverlay from '@components/SearchInjection';
import Diversity from './Diversity';
import {checkIsTechDrive} from './utils';
import {HIRIST_FUNCTIONAL_AREAS} from '@jobseeker/feature-onboarding/lib/constants';

const getQueryParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  return Object.fromEntries(new URLSearchParams(window?.location?.search));
};

const InsightsModal = dynamic(
  () => import('@jobseeker/feature-job/lib/components/InsightsModal/V2'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const AdsPanelHarness = memo(
  dynamic(
    () =>
      import('@jobseeker/client-harness/lib/components/AdsHarness/AdsPanelV2'),
    {
      loading: () => <Loader />,
    }
  )
);

const JobFeedHarness = dynamic(
  () => import('@jobseeker/client-harness/lib/components/JobHarness/JobfeedV2'),
  {
    loading: () => <Loader />,
  }
);

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/lib/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const DownloadAppJobdetail = dynamic(
  () =>
    import('@jobseeker/lotus/lib/components/DownloadAppJobdetailAndCatogary'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const ResumePopup = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/lib/components/ProfileHarness/ResumePopup'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type Testimonial = {
  image: string;
  name: string;
  about: string;
};

type DiversityData = {
  diversityId: number;
  diversityName: string;
  diversityUrl: string;
  testimonials: Testimonial[];
};

const getVideoId = (url = '') => {
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length == 11 ? match[7] : '';
};

type JobDetailProps = {
  jobId: string;
  categoryName: string;
  refInfo: string | undefined;
  isMobile?: boolean;
};

const HiristJobDetail = (props: JobDetailProps) => {
  const {jobId, categoryName, refInfo, isMobile = false} = props;

  const jobInfo = useSelector((state: AppState) => state.jobDetail) as JB & {
    magicRank: number;
    careerCompanyName?: string;
    hasExpired?: boolean;
    permanentlyRemoved?: boolean;
  };

  const {
    userId = '',
    isLoggedIn = false,
    isPro,
  } = useSelector((state: AppState) => state.auth);

  const userData = useSelector((state: AppState) => state.user, shallowEqual);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  const {
    min,
    max,
    hits,
    tags,
    title,
    active,
    premium,
    videoUrl,
    showcase,
    diversity,
    exDefence,
    introText,
    locations,
    magicRank,
    applyCount,
    categoryId,
    createdTime,
    tagIdString,
    confidential,
    workFromHome,
    functionalArea,
    femaleCandidate,
    differentlyAbled,
    hasExpired = false,
    followUpStatus = 0,
    femaleBackWorkForce,
    recruiter: {recruiterId = 0} = {recruiterId: 0},
  } = jobInfo;

  const {diversityUrl = '', testimonials = []} = (diversity ??
    {}) as DiversityData;

  const {image, name: fullName, about} = testimonials[0] ?? {};

  const [name, desig] = fullName?.split(',') ?? ['', ''];

  const diversityData = {
    diversityUrl,
    image,
    about: about?.slice(0, 400) + '...',
    name: name.trim(),
    designation: desig?.trim(),
  };

  const diversityArray = [];
  if (femaleCandidate) diversityArray.push('Women candidates preferred');
  if (differentlyAbled)
    diversityArray.push('Differently-abled candidates preferred');
  if (exDefence) diversityArray.push('Ex-defence personnel preferred');
  if (workFromHome) diversityArray.push('May work from home');
  if (femaleBackWorkForce)
    diversityArray.push('For women joining back the workforce');

  const {
    profile: {resumeUpdated, email},
  } = userData;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const [insightsOpen, setInsightsOpen] = useState(false);
  const {enableLoginPopup = false} = useSelector(
    (state: AppState) => state.app.layout
  );
  const [openReportJobDialog, setOpenReportJobDialog] = useState(false);
  const [followup, setFollowedUp] = useState(followUpStatus !== 0);
  const [openResumeBanner, setOpenResumeBanner] = useState(false);
  const [resumeBannerSuccess, setResumeBannerSuccess] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const targetRef = useRef(null);

  const isDeviceLarge = useIsLargeDevice('md');
  const isDeviceMedium = useIsLargeDevice();

  useEffect(() => {
    if (!jobId || !userId) return;

    const callApis = () => {
      Promise.all([
        dispatch(
          fetchSimilarJobs({
            userId,
            tags: tagIdString ? tagIdString.split(' ') : [],
            exp: min,
            min,
            max,
            id: jobId,
            cat: categoryId,
            whatFor: 'hirist',
            functionalArea,
          })
        ),
        dispatch(getMagicRank({jobId, userId: parseInt(userId), headers: {}})),
      ]).catch(error => console.log('🚀 ~ Error fetching job data:', error));
    };

    const DEBOUNCE_DELAY = 250;
    const timer = setTimeout(callApis, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [
    jobId,
    userId,
    tagIdString,
    min,
    categoryId,
    dispatch,
    functionalArea,
    max,
  ]);

  const {jobversion = '', jobpos = '', ref = ''} = getQueryParams();

  const router = useRouter();

  useEffect(() => {
    if (resumeUpdated && email && resumeBannerSuccess) {
      setOpenResumeBanner(false);
    }
  }, [resumeUpdated, email, resumeBannerSuccess]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowApply(!entry.isIntersecting),
      {
        threshold: 0,
      }
    );

    const temp = targetRef.current;

    if (temp) {
      observer.observe(temp);
    }

    return () => {
      if (temp) {
        observer.unobserve(temp);
      }
    };
  }, []);

  useEffect(() => {
    const sendTrackingEvents = () => {
      const userId = sessionStorage.getItem('userId') || '';

      handleTracking('ViewJobDescription', {
        event_category: 'Job',
        Source: 'JobDescription',
        UserId: userId,
        Jobid: jobId,
        RecruiterId: recruiterId,
        Device: sessionStorage.getItem('device') || 'desktop',
        Origin: router.pathname,
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });

      if (showcase) {
        handleTracking('scViewCompanyShowcaseImage', {
          event_category: 'Showcase',
          Source: 'JobDescription',
          CompanyId: showcase.showcaseCompanyId || 0,
          UserId: userId,
          Jobid: jobId,
          RecruiterId: recruiterId,
          Device: sessionStorage.getItem('device') || 'desktop',
          Origin: router.pathname,
          Status: sessionStorage.getItem('status') || 'LoggedOut',
        });

        TrackEventUBA('companyPhotoCarouselClick', 'click', {
          page: getUbaPageName(),
          jobID: jobId,
          categoryID: categoryId,
          tagID: '',
          companyID: showcase.showcaseCompanyId || '',
          companyName: jobInfo?.companyData?.companyName,
          sessionUsername: userId,
        });
      }
    };

    const timeoutId = setTimeout(sendTrackingEvents, 500);

    if (
      (typeof navigator !== 'undefined' && navigator.onLine) ||
      typeof navigator === 'undefined'
    ) {
      const isTechDrive = checkIsTechDrive();

      if (isTechDrive) {
        handleTracking('JD Page View', {
          event_category: 'Offline Landing page',
          view: 1,
        });
      }
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    categoryId,
    categoryName,
    jobId,
    jobpos,
    jobversion,
    recruiterId,
    ref,
    title,
    router.pathname,
    showcase,
    jobInfo?.companyData?.companyName,
  ]);

  const handleApply = () => {
    TrackEventUBA('applyClick', 'click', {
      pageName: 'Job Description Page',
      jobID: jobInfo.id,
      jobVersion: '',
      jobPos: '',
      page: 'JD',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });

    handleJobApplicationOnHirist({
      router,
      jobInfo,
      refInfo,
      userData,
      isLoggedIn,
      performLogoutAction,
      setOpenResumeBanner,
    });
  };

  useEffect(() => {
    if (!isLoggedIn || !loginSuccess || !userData) {
      return;
    }

    handleApply();
  }, [isLoggedIn, loginSuccess, userData]);

  const jobFeedStore = useSelector(
    (state: AppState) => state.job,
    shallowEqual
  );

  const {
    proMembership, // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }: any = useInterceptor();

  if (!jobInfo.companyData || !jobInfo.recruiter) {
    return <Header />;
  }

  if (hasExpired) {
    if (isCSR()) {
      openUrl(`/c/${cleanUrl(categoryName)}-jobs?ref=${ref}&expJD=1`, '_self');
    }

    return;
  }

  const {
    companyData: {companyName = '', companyStatus, companyLogoFilePath = ''},
    recruiter: {
      recruiterName,
      profilePicUrl,
      designation,
      lastLogin = '',
      recruiterActions,
    },
    careerCompanyName = '',
  } = jobInfo;

  const experience = `${min} - ${max} Years`;

  const postedOn = moment(createdTime).format('DD/MM/YYYY');

  const routeToJobDetail = (jobSlug: string, jobId: string | number) => {
    openUrl(`${ROUTE_JOB_DETAIL}/${jobSlug}-${jobId}`);
  };

  const performLogoutAction = () => {
    dispatch({
      type: 'AppContext/set_login_popup',
      payload: true,
    });
  };

  const openInsightsDialog = () => {
    if (!isLoggedIn) {
      performLogoutAction();
      return;
    }

    if (isPro) {
      dispatch(
        fetchJobInsights({
          userId: userId,
          jobId: jobId,
        })
      );
    }

    setInsightsOpen(true);

    handleTracking('ClickInsights', {
      JobId: jobId,
      Ref: ref,
      Origin: router.pathname,
      event_category: 'Job',
      Source: 'JobDescription',
      UserId: sessionStorage.getItem('userId') ?? 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      CategoryId: categoryId,
      RecruiterId: recruiterId,
    });

    TrackEventUBA('jdPageClick', 'click', {
      section: 'Insights',
      jobID: jobId,
      ref,
      jobPos: jobpos,
      jobVersion: jobversion,
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  };

  const onJobSave = (jobId: number) => {
    dispatch(
      saveJob({
        jobId,
        unsave: !!jobInfo.saved,
        userId,
      })
    );
  };

  const saveJobForLogged = () => {
    if (!isLoggedIn) {
      performLogoutAction();
      return;
    }

    onJobSave(jobInfo.id);
  };

  const sendEvent = (
    eventCatogary: string,
    eventName: string,
    action: string
  ) => {
    handleTracking(`${eventName}_${action}`, {
      event_category: `${eventCatogary}`,
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  const handleJobFollowup = (jobId: string) => {
    handleTracking('ClickFollowUp', {
      event_category: 'Job',
      Origin: router.pathname,
      Source: 'JobDescription',
      CategoryId: jobInfo.categoryId,
      JobId: jobInfo.id,
      RecruiterId: jobInfo.recruiter?.recruiterId,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });
    TrackEventUBA('followUpClick', 'click', {
      pageName: 'Job Description Page',
      jobID: jobInfo.id,
      jobVersion: '',
      jobPos: '',
      page: 'JD',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    if (!isLoggedIn) {
      performLogoutAction();
      return;
    }

    if (jobInfo?.applied) {
      (async () => {
        try {
          const followUp = await jobDetailService.doFollowUp({jobId});
          if (typeof followUp === 'boolean') {
            // await userService.updateFollowUp();
            setFollowedUp(followUp);
          }
        } catch (error: unknown) {
          console.log('🚀 ~ file: index.tsx:298 ~ error:', error);
        }
      })();
    }
  };

  const onLoginSuccess = () => {
    if (isCSR()) {
      if (showApply) {
        dispatch({
          type: 'AppContext/set_login_popup',
          payload: false,
        });

        dispatch({
          type: 'Auth/set_user_loggedin',
        });

        setLoginSuccess(true);

        return;
      }
      window.location.reload();
    }
  };

  return (
    <>
      <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
      {!isDeviceLarge && showApply && (
        <Box position="fixed" bottom="0" width="100%" zIndex={1}>
          <ApplyButton
            handleApply={handleApply}
            saveJobForLogged={saveJobForLogged}
            openInsightsDialog={openInsightsDialog}
            handleJobFollowup={handleJobFollowup}
            jobSaved={!!jobInfo.saved}
            jobApplied={!!jobInfo.applied}
            jobFollowedUp={followup}
            jobId={jobId}
            showLoader={showLoader}
            setShowLoader={setShowLoader}
          />
        </Box>
      )}
      <Box ref={targetRef}>
        <Header
          title={title}
          company={companyName}
          confidential={confidential}
          locations={locations}
          experience={experience}
          tags={tags.map(tag => tag.name)}
          postedOn={postedOn}
          showcase={showcase ?? {}}
          premium={premium}
          active={Boolean(active)}
          companyStatus={companyStatus}
          careerCompanyName={careerCompanyName}
          companyLogoFilePath={companyLogoFilePath}
          isMobile={isMobile}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={!isMobile ? 'row' : 'column'}
        gap={!isMobile ? 7 : 2}
        px={!isMobile ? 6 : 0}
      >
        <Box>
          <Typography
            component="h2"
            mt={3}
            mb={1.25}
            fontSize={22}
            fontWeight={700}
            ml={!isMobile ? 0 : 1}
          >
            Job Description
          </Typography>

          <Box
            className="details-container"
            maxWidth={925}
            bgcolor="white"
            borderRadius="8px"
            py={1}
            px={3}
            mb={!isMobile ? 7 : 0}
          >
            {videoUrl.length > 0 && (
              <Box className="youtubePlay" mt={2}>
                <iframe
                  src={`https://www.youtube-noCookie.com/embed/${getVideoId(
                    videoUrl
                  )}`}
                  allowFullScreen
                  style={{
                    width: '100%',
                    height: isDeviceLarge || isDeviceMedium ? 432 : 250,
                  }}
                />
              </Box>
            )}
            <Typography
              component="div"
              dangerouslySetInnerHTML={{__html: introText}}
              sx={{
                fontSize: 16,
                pt: 1.5,
                color: 'rgba(0, 0, 0, 0.7)',
                lineHeight: '20px',
                '& b': {
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#000',
                  opacity: 1,
                },
                '& p': {
                  margin: 0,
                  fontSize: 16,
                  fontFamily: 'inherit',
                  color: 'rgba(0, 0, 0, 0.7)',
                  overflowWrap: 'break-word',
                  '& b': {
                    fontSize: 18,
                    fontWeight: 600,
                    color: '#000',
                    opacity: 1,
                  },
                  '@media (max-width: 1023px)': {
                    margin: 0,
                    fontSize: 16,
                    fontFamily: 'inherit',
                    color: 'rgba(0, 0, 0, 0.7)',
                  },
                },
              }}
            />
            {testimonials.length > 0 && <Diversity data={diversityData} />}

            {diversityArray.length > 0 && (
              <Box
                mt={testimonials.length > 0 ? 3 : 6}
                mb={2}
                pt={1.5}
                borderTop="0.1px solid rgba(0, 0, 0, 0.1)"
                borderBottom="0.1px solid rgba(0, 0, 0, 0.1)"
                width="calc(100% + 48px)"
                position="relative"
                left="-24px"
                pl={3}
              >
                <Typography
                  sx={{opacity: 0.7}}
                  display="inline-block"
                  mr={1}
                  fontSize={14}
                >
                  The job is for:{' '}
                </Typography>
                {diversityArray.map((data, idx) => (
                  <Box
                    sx={{opacity: 0.4, fontSize: 14}}
                    key={idx}
                    display="inline-block"
                    border="1px solid rgb(224, 224, 224)"
                    borderRadius="16px"
                    px={2}
                    height={32}
                    lineHeight={2}
                    mb={1.5}
                    mr={0.5}
                  >
                    {data}
                  </Box>
                ))}
              </Box>
            )}

            <ApplyBtns
              handleApply={handleApply}
              saveJobForLogged={saveJobForLogged}
              openInsightsDialog={openInsightsDialog}
              handleJobFollowup={handleJobFollowup}
              jobSaved={!!jobInfo.saved}
              jobApplied={!!jobInfo.applied}
              jobFollowedUp={followup}
              jobId={jobId}
              showApply={!isMobile}
              showLoader={showLoader}
              setShowLoader={setShowLoader}
            />
            <Box
              sx={{
                borderTop: '1px solid rgba(128, 128, 128, 0.7)',
                width: 'calc(100% + 48px)',
                position: 'relative',
                left: '-24px',
                my: 2,
              }}
            />
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Image
                  src={HiristInfoIcon}
                  alt="info-icon"
                  height={18}
                  width={18}
                />
                <Typography
                  fontSize={14}
                  fontWeight={300}
                  height="fit-content"
                  mt={0.3}
                  ml={0.3}
                >
                  Did you find something suspicious?{' '}
                  <Button
                    onClick={() => {
                      setOpenReportJobDialog(true);
                    }}
                    sx={{
                      color: 'rgb(0, 87, 255)',
                      fontWeight: 700,
                      p: 0,
                    }}
                    disableRipple
                  >
                    Report Here!
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>

          {!isMobile && jobFeedStore?.jobfeed?.length > 0 && (
            <Box mb={{xxs: 2, md: 0}} maxWidth={925}>
              <JobFeedHarness
                routeToJobDetail={routeToJobDetail}
                title={
                  <Typography component="h2" fontSize={16} mb={1.5}>
                    Similar jobs that you might be interested in
                  </Typography>
                }
                feedOnly={true}
                infiniteScroll={false}
                injectAsChild={true}
                showSmallBadges={false}
                showProfileCard={false}
                variant={VersionVariants.VERSION_TWO}
              />
            </Box>
          )}
        </Box>
        <Box
          mt={!isMobile ? 3 : 0}
          pt={!isMobile ? 5.5 : 0}
          maxWidth={!isMobile ? 365 : '100%'}
        >
          <Box bgcolor="white" p={2} borderRadius="8px" mb={3}>
            <JobOverviewCard
              actionUrl={
                !jobInfo?.confidential
                  ? `/r/${jobInfo?.recruiter?.recruiterId}-${cleanUrl(
                      jobInfo?.recruiter?.recruiterName.replace(/&/g, '-and-')
                    )}`
                  : '#'
              }
              variant={VersionVariants.VERSION_TWO}
              jobId={jobId}
              jobViews={hits}
              applications={applyCount}
              recruiterActions={recruiterActions}
              magicSortRank={magicRank}
              category={{label: categoryName, tag: categoryName}}
              lastLogin={moment(lastLogin || new Date()).format('D MMM YYYY')}
              position={confidential ? '' : `${designation} at ${companyName}`}
              recruiterName={
                confidential ? 'Verified Recruiter' : recruiterName
              }
              recruiterImgUrl={
                confidential ? '/resources/img/user.jpg' : profilePicUrl
              }
              enableMagicRank={1}
              disableInsights
              functionalArea={
                HIRIST_FUNCTIONAL_AREAS.find(fa => fa.value === functionalArea)
                  ?.label
              }
              showRecommended
              postedOn={moment(createdTime).toISOString()}
            />
          </Box>
          {!isMobile && (
            <Box>
              <AdsPanelHarness
                variant={VersionVariants.VERSION_TWO}
                showSmallBadges={false}
                showFeatured={false}
                showLearning={true}
                showLearningCenterCTA={false}
                showLearnGrow={true}
                page={JOBDETAIL}
              />
              <JobsByLocation />
              <DownloadAppJobdetail sendEvent={sendEvent} />
              <Box mt={3.75}>
                <BlogsPanel heading="Interview Questions for you" />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {enableLoginPopup && (
        <HiristAuthentication
          variant={VersionVariants.VERSION_TWO}
          onSuccess={onLoginSuccess}
          openDialog={enableLoginPopup}
          enableSignIn={true}
          setOpenDialog={() => {
            setShowLoader(false);
            dispatch({
              type: 'AppContext/set_login_popup',
              payload: false,
            });
          }}
        />
      )}

      {insightsOpen && (
        <InsightsModal
          handleClose={() => setInsightsOpen(false)}
          open={insightsOpen}
          variant={VersionVariants.VERSION_TWO}
          proUser={isPro}
          proMeta={proMembership?.insightsBanner}
        />
      )}

      {openReportJobDialog && (
        <ReportJobDialog
          open={openReportJobDialog}
          onClose={() => setOpenReportJobDialog(false)}
          variant={VersionVariants.VERSION_THREE}
        />
      )}

      {openResumeBanner && (
        <ResumePopup
          open={openResumeBanner}
          handleCTA={() => {
            console.log('Handled Called');
            setResumeBannerSuccess(true);
          }}
          onClose={() => setOpenResumeBanner(false)}
        />
      )}

      {!isDeviceLarge && isMobile && jobFeedStore?.jobfeed?.length > 0 && (
        <Box
          mb={{xxs: 2, md: 0}}
          position="relative"
          left={{xxs: 0, md: '-40px'}}
        >
          <JobFeedHarness
            routeToJobDetail={routeToJobDetail}
            title={
              <Typography mt={2}>
                Similar jobs that you might be interested in
              </Typography>
            }
            feedOnly={true}
            infiniteScroll={false}
            injectAsChild={true}
            showSmallBadges={false}
            showProfileCard={false}
            variant={VersionVariants.VERSION_TWO}
          />
        </Box>
      )}
    </>
  );
};

export default HiristJobDetail;
